import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LOGIN } from '../constants/api';
import { MESSAGES, CSS_CLASSES, FILE_PATHS, DEFAULTS } from '../constants/app';
import { validation, apiUtils, errorUtils } from '../utils';
import type { LoginCredentials, LoginResponse } from '../types';
import { Spinner } from './loading/LoadingIndicators';

interface LoginProps {
  onLogin: (email: string, role: 'admin' | 'super_admin', hospitalName?: string, userId?: string, token?: string) => void;
}

export default function Login({ onLogin }: LoginProps) {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState<LoginCredentials>({ email: '', password: '' });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const updateCredentials = (field: keyof LoginCredentials, value: string) => {
    setCredentials(prev => ({ ...prev, [field]: value }));
    if (error) setError(''); // Clear error when user starts typing
  };

  const validateCredentials = (): string | null => {
    if (!validation.isRequired(credentials.email)) {
      return MESSAGES.VALIDATION.REQUIRED_FIELD;
    }
    if (!validation.isValidEmail(credentials.email)) {
      return MESSAGES.VALIDATION.INVALID_EMAIL;
    }
    if (!validation.isRequired(credentials.password)) {
      return MESSAGES.VALIDATION.REQUIRED_FIELD;
    }
    return null;
  };

  const handleHospitalLogin = async (): Promise<void> => {
    try {
      const response = await fetch(LOGIN, apiUtils.createRequestOptions('POST', credentials));
      const result: LoginResponse = await apiUtils.handleResponse(response);
      const hospitalName = result.data.full_name || DEFAULTS.HOSPITAL_NAME;
      const userId = result.data._id;
      const role = result.data.role; // Extract role from API response
      const token = result.data.token; // Extract token from response
      onLogin(credentials.email, role, hospitalName, userId, token);
    } catch (error) {
      // apiUtils.handleResponse already extracts error messages properly
      // Re-throw with extracted error message
      throw new Error(errorUtils.getErrorMessage(error));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Validate credentials
    const validationError = validateCredentials();
    if (validationError) {
      setError(validationError);
      return;
    }
    
    setIsLoading(true);

    try {
      // Use API login for all users (static login removed)
      await handleHospitalLogin();
      
    } catch (error) {
      errorUtils.logError(error, 'Login');
      let errorMessage = errorUtils.getErrorMessage(error);
      
      // Handle network errors
      if (errorUtils.isNetworkError(error)) {
        errorMessage = MESSAGES.ERRORS.LOGIN_FAILED;
      }
      
      // Ensure invalid credentials message is shown for 401 errors
      if (errorMessage.toLowerCase().includes('401') || 
          errorMessage.toLowerCase().includes('invalid email or password')) {
        errorMessage = MESSAGES.ERRORS.INVALID_CREDENTIALS;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const inputClassName = (hasError: boolean) =>
    `${CSS_CLASSES.INPUTS.PATIENT} ${hasError ? CSS_CLASSES.INPUTS.ERROR : ''}`;

  return (
    <div className={CSS_CLASSES.AUTH.PAGE}>
      <div className={CSS_CLASSES.AUTH.CARD}>
       {/* Header */}
       <div className="text-center mb-8">
          <div className="mb-4">
            <img 
              src={FILE_PATHS.IMAGES.HELIUS_LOGO}
              alt="Helius" 
              className="w-24 h-24 sm:w-30 sm:h-30 mx-auto"
            />
          </div>
          <p className={CSS_CLASSES.AUTH.TITLE}>Welcome to Helius Clinician Platform</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email Field */}
          <div>
            <label htmlFor="email" className={CSS_CLASSES.AUTH.LABEL}>
              Email
            </label>
            <input
              id="email"
              type="email"
              value={credentials.email}
              onChange={(e) => updateCredentials('email', e.target.value)}
              placeholder="Email address"
              className={inputClassName(!!error)}
              disabled={isLoading}
              required
            />
          </div>

          {/* Password Field */}
          <div>
            <label htmlFor="password" className={CSS_CLASSES.AUTH.LABEL}>
              Password
            </label>
            <input
              id="password"
              type="password"
              value={credentials.password}
              onChange={(e) => updateCredentials('password', e.target.value)}
              placeholder="Password"
              className={inputClassName(!!error)}
              disabled={isLoading}
              required
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className={CSS_CLASSES.TEXT.ERROR + ' text-center'}>
              {error}
            </div>
          )}

          {/* Login Button */}
          <button
            type="submit"
            disabled={isLoading}
            className={`${CSS_CLASSES.AUTH.SUBMIT} ${
              isLoading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isLoading ? (
              <span className="inline-flex items-center justify-center gap-2">
                <Spinner size="md" variant="onPrimary" />
                {MESSAGES.LOADING.SIGNING_IN}
              </span>
            ) : (
              'Login'
            )}
          </button>

          {/* Register Link */}
          <div className="text-center hidden">
            <p className={CSS_CLASSES.AUTH.TITLE}>
              Don't have an account?{' '}
              <button
                type="button"
                onClick={() => navigate('/register')}
                className={CSS_CLASSES.AUTH.LINK}
              >
                Create account
              </button>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
